import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useDataSync, useRouteRefresh } from '../context/DataSyncContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import type { Report, ReportStatus } from '../types';
import '../styles/admin-reports.css';

const STATUS_OPTIONS: Array<{ value: ReportStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' }
];

function statusBadgeClass(status: ReportStatus) {
  switch (status) {
    case 'open': return 'admin-reports__badge--open';
    case 'in_progress': return 'admin-reports__badge--progress';
    case 'resolved': return 'admin-reports__badge--resolved';
    default: return '';
  }
}

function statusLabel(status: ReportStatus) {
  switch (status) {
    case 'open': return 'Open';
    case 'in_progress': return 'In Progress';
    case 'resolved': return 'Resolved';
    default: return status;
  }
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function timeAgo(isoString: string) {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AdminReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('open');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const { refreshAllBadges } = useDataSync();
  const refreshToken = useRouteRefresh('/admin/reports');
  const autoRefreshTick = useAutoRefresh(5000, { enabled: user?.role === 'admin' });

  const fetchReports = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : undefined;
      const data = await api.reports(params);
      setReports(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports, autoRefreshTick, refreshToken]);

  const openReport = useCallback(async (id: string) => {
    setDetailLoading(true);
    setSaveMsg(null);
    try {
      const data = await api.report(id);
      setSelectedReport(data);
      setAdminNotes(data.adminNotes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const updateStatus = useCallback(async (newStatus: ReportStatus) => {
    if (!selectedReport) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await api.updateReport(selectedReport.id, { status: newStatus });
      setSelectedReport(updated);
      void fetchReports();
      void refreshAllBadges();
      setSaveMsg('Status updated');
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [selectedReport, fetchReports, refreshAllBadges]);

  const saveNotes = useCallback(async () => {
    if (!selectedReport) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await api.updateReport(selectedReport.id, { adminNotes });
      setSelectedReport(updated);
      setSaveMsg('Notes saved');
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [selectedReport, adminNotes]);

  if (user?.role !== 'admin') {
    return <div className="admin-reports"><p>Admin access required.</p></div>;
  }

  return (
    <div className="admin-reports">
      <div className="admin-reports__header">
        <h1>Issue Reports</h1>
      </div>

      <div className="admin-reports__filters">
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`admin-reports__filter-btn${statusFilter === opt.value ? ' active' : ''}`}
            onClick={() => { setStatusFilter(opt.value); setSelectedReport(null); }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && <div className="admin-reports__error">{error}</div>}

      <div className="admin-reports__layout">
        <div className="admin-reports__list">
          {loading && reports.length === 0 && <div className="admin-reports__loading">Loading...</div>}
          {!loading && reports.length === 0 && (
            <div className="admin-reports__empty">No reports found.</div>
          )}
          {reports.map(r => (
            <div
              key={r.id}
              className={`admin-reports__row${selectedReport?.id === r.id ? ' selected' : ''}`}
              onClick={() => openReport(r.id)}
            >
              <div className="admin-reports__row-main">
                <span className={`admin-reports__badge ${statusBadgeClass(r.status)}`}>
                  {statusLabel(r.status)}
                </span>
                <span className="admin-reports__row-comment">
                  {r.comment.length > 100 ? r.comment.slice(0, 100) + '...' : r.comment}
                </span>
              </div>
              <div className="admin-reports__row-meta">
                <span>{r.createdByName}</span>
                <span>{timeAgo(r.createdAt)}</span>
                {r.context?.capturedRequests?.length > 0 && (
                  <span className="admin-reports__row-captures">
                    {r.context.capturedRequests.length} capture{r.context.capturedRequests.length !== 1 ? 's' : ''}
                  </span>
                )}
                {r.attachments && r.attachments.length > 0 && (
                  <span className="admin-reports__row-captures">
                    {r.attachments.length} file{r.attachments.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="admin-reports__detail">
          {detailLoading && <div className="admin-reports__loading">Loading report...</div>}
          {!detailLoading && !selectedReport && (
            <div className="admin-reports__detail-empty">Select a report to view details</div>
          )}
          {!detailLoading && selectedReport && (
            <>
              <div className="admin-reports__detail-section">
                <h3>Report</h3>
                <div className="admin-reports__detail-meta">
                  <span>From: <strong>{selectedReport.createdByName}</strong></span>
                  <span>{new Date(selectedReport.createdAt).toLocaleString()}</span>
                </div>
                <p className="admin-reports__detail-comment">{selectedReport.comment}</p>
              </div>

              <div className="admin-reports__detail-section">
                <h3>Status</h3>
                <div className="admin-reports__status-controls">
                  {(['open', 'in_progress', 'resolved'] as ReportStatus[]).map(s => (
                    <button
                      key={s}
                      className={`admin-reports__status-btn${selectedReport.status === s ? ' active' : ''}`}
                      onClick={() => updateStatus(s)}
                      disabled={saving || selectedReport.status === s}
                    >
                      {statusLabel(s)}
                    </button>
                  ))}
                </div>
                {selectedReport.resolvedByName && (
                  <div className="admin-reports__resolved-info">
                    Resolved by {selectedReport.resolvedByName} on{' '}
                    {selectedReport.resolvedAt ? new Date(selectedReport.resolvedAt).toLocaleString() : ''}
                  </div>
                )}
              </div>

              {selectedReport.context && (
                <div className="admin-reports__detail-section">
                  <h3>Context</h3>
                  <div className="admin-reports__context-grid">
                    {selectedReport.context.extensionVersion && (
                      <div><label>Extension</label><span>v{selectedReport.context.extensionVersion}</span></div>
                    )}
                    {selectedReport.context.browserInfo && (
                      <div><label>Browser</label><span className="admin-reports__browser-info">{selectedReport.context.browserInfo}</span></div>
                    )}
                    {selectedReport.context.pageUrl && (
                      <div><label>Page URL</label><span className="admin-reports__page-url">{selectedReport.context.pageUrl}</span></div>
                    )}
                    {selectedReport.context.pageTitle && (
                      <div><label>Page Title</label><span>{selectedReport.context.pageTitle}</span></div>
                    )}
                  </div>
                </div>
              )}

              {selectedReport.context?.capturedRequests?.length > 0 && (
                <div className="admin-reports__detail-section">
                  <h3>Captured Requests ({selectedReport.context.capturedRequests.length})</h3>
                  {selectedReport.context.capturedRequests.map((req, i) => (
                    <div key={i} className="admin-reports__capture">
                      <div className="admin-reports__capture-header">
                        <span className="admin-reports__capture-method">{req.method}</span>
                        <span className="admin-reports__capture-url">{req.url}</span>
                        {req.timestamp && (
                          <span className="admin-reports__capture-time">
                            {new Date(req.timestamp).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                      {req.warnings?.length > 0 && (
                        <div className="admin-reports__capture-warnings">
                          {req.warnings.map((w, j) => (
                            <div key={j} className="admin-reports__warning-item">
                              <span className={`admin-reports__sev-dot admin-reports__sev-dot--${w.severity}`} />
                              <span className="admin-reports__warning-msg">{w.message}</span>
                              <span className="admin-reports__warning-rule">{w.rule}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Legacy screenshot for old reports */}
              {selectedReport.screenshot && !selectedReport.attachments?.length && (
                <div className="admin-reports__detail-section">
                  <h3>Screenshot</h3>
                  <img
                    className="admin-reports__screenshot"
                    src={`data:image/png;base64,${selectedReport.screenshot}`}
                    alt="Screenshot"
                  />
                </div>
              )}

              {/* Attachments */}
              {selectedReport.attachments && selectedReport.attachments.length > 0 && (
                <div className="admin-reports__detail-section">
                  <h3>Attachments ({selectedReport.attachments.length})</h3>
                  <div className="admin-reports__attachments">
                    {selectedReport.attachments.map((att, i) => {
                      const isImage = att.type.startsWith('image/');
                      return (
                        <div key={i} className="admin-reports__attachment-item">
                          {isImage && att.data ? (
                            <img
                              className="admin-reports__screenshot"
                              src={`data:${att.type};base64,${att.data}`}
                              alt={att.name}
                            />
                          ) : att.data ? (
                            <div className="admin-reports__attachment-file">
                              <span className="admin-reports__attachment-icon">
                                {att.type.includes('json') ? '{ }' : 'TXT'}
                              </span>
                              <a
                                href={`data:${att.type};base64,${att.data}`}
                                download={att.name}
                                className="admin-reports__attachment-download"
                              >
                                Download
                              </a>
                            </div>
                          ) : null}
                          <div className="admin-reports__attachment-meta">
                            <span className="admin-reports__attachment-name">{att.name}</span>
                            <span className="admin-reports__attachment-size">{formatSize(att.size)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="admin-reports__detail-section">
                <h3>Admin Notes</h3>
                <textarea
                  className="admin-reports__notes"
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this report..."
                  rows={4}
                />
                <button
                  className="admin-reports__save-btn"
                  onClick={saveNotes}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Notes'}
                </button>
                {saveMsg && <span className="admin-reports__save-msg">{saveMsg}</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
